// Google Tag Manager Utility Service
import gtmLoader from './gtmLoader';
import googleAdsLoader from './googleAdsLoader';

class GTMService {
  constructor() {
    this.isInitialized = false;
    this.gtmId = process.env.REACT_APP_GTM_ID;
    this.googleAdsId = process.env.REACT_APP_GOOGLE_ADS_ID;
    this.dataLayer = window.dataLayer || [];

    // Initialize dataLayer if not exists
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }

  // Initialize GTM with dynamic loading
  async initialize() {
    if (this.isInitialized) {
      return true;
    }

    try {
      // IMPORTANT: Load Google Ads BEFORE GTM
      // This ensures the base Google tag is available for conversion tracking
      if (this.googleAdsId) {
        try {
          await googleAdsLoader.loadGoogleAdsScript();
          if (googleAdsLoader.getConfig().debugMode) {
            console.log(
              '✅ Google Ads loaded before GTM:',
              googleAdsLoader.getConfig()
            );
          }
        } catch (adsError) {
          console.error(
            '⚠️ Google Ads failed to load (non-critical):',
            adsError
          );
          // Continue with GTM initialization even if Ads fails
        }
      }

      // Load GTM script dynamically with environment validation
      await gtmLoader.loadGTMScript();

      this.isInitialized = true;

      // Push initial configuration
      this.pushToDataLayer({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      // Log successful initialization
      const config = gtmLoader.getConfig();
      if (config.debugMode) {
        console.log('✅ GTM Service initialized:', config);
      }

      return true;
    } catch (error) {
      console.error('❌ Failed to initialize GTM:', error);
      return false;
    }
  }

  // Push data to dataLayer
  pushToDataLayer(data) {
    if (window.dataLayer) {
      window.dataLayer.push(data);
    }
  }

  // Check if GTM is ready for tracking
  isReady() {
    return this.isInitialized && gtmLoader.isReady();
  }

  // Environment-aware tracking methods with safety checks

  // Track page view
  trackPageView(pagePath, pageTitle) {
    if (!this.isReady()) {
      if (gtmLoader.getConfig().debugMode) {
        console.warn('⚠️ GTM not ready for page view tracking');
      }
      return;
    }

    this.pushToDataLayer({
      event: 'page_view',
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.href,
      environment: process.env.REACT_APP_CURRENT_ENV
    });
  }

  // Track custom events
  trackEvent(eventName, parameters = {}) {
    if (!this.isReady()) {
      if (gtmLoader.getConfig().debugMode) {
        console.warn(`⚠️ GTM not ready for event: ${eventName}`);
      }
      return;
    }

    this.pushToDataLayer({
      event: eventName,
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...parameters
    });
  }

  // Track user interactions
  trackClick(elementName, category = 'User Interaction') {
    this.trackEvent('click', {
      event_category: category,
      event_label: elementName,
      element_name: elementName
    });
  }

  // Track form submissions
  trackFormSubmit(formName, formType = 'general') {
    this.trackEvent('form_submit', {
      event_category: 'Form',
      event_label: formName,
      form_name: formName,
      form_type: formType
    });
  }

  // Track scroll depth
  trackScrollDepth(scrollPercent) {
    this.trackEvent('scroll', {
      event_category: 'Engagement',
      event_label: `${scrollPercent}%`,
      scroll_depth: scrollPercent
    });
  }

  // Track file downloads
  trackDownload(fileName, fileType) {
    this.trackEvent('file_download', {
      event_category: 'Download',
      event_label: fileName,
      file_name: fileName,
      file_type: fileType
    });
  }

  // Track outbound links
  trackOutboundLink(url, linkText) {
    this.trackEvent('outbound_click', {
      event_category: 'Outbound Link',
      event_label: linkText || url,
      outbound_url: url
    });
  }

  // Track video interactions
  trackVideoPlay(videoTitle, videoDuration = null) {
    this.trackEvent('video_play', {
      event_category: 'Video',
      event_label: videoTitle,
      video_title: videoTitle,
      video_duration: videoDuration
    });
  }

  // Track search
  trackSearch(searchTerm, searchResults = null) {
    this.trackEvent('search', {
      event_category: 'Search',
      event_label: searchTerm,
      search_term: searchTerm,
      search_results: searchResults
    });
  }

  // Track business-specific events for Solfin

  // Track loan application start
  trackLoanApplicationStart(loanType, amount = null) {
    this.trackEvent('loan_application_start', {
      event_category: 'Loan Application',
      event_label: loanType,
      loan_type: loanType,
      loan_amount: amount
    });
  }

  // Track loan application completion
  trackLoanApplicationComplete(loanType, amount = null, applicationId = null) {
    this.trackEvent('loan_application_complete', {
      event_category: 'Loan Application',
      event_label: loanType,
      loan_type: loanType,
      loan_amount: amount,
      application_id: applicationId
    });
  }

  // Track calculator usage
  trackCalculatorUse(calculatorType, inputValues = {}) {
    this.trackEvent('calculator_use', {
      event_category: 'Calculator',
      event_label: calculatorType,
      calculator_type: calculatorType,
      ...inputValues
    });
  }

  // Track user registration
  trackUserRegistration(userType, registrationMethod = 'email') {
    this.trackEvent('sign_up', {
      event_category: 'User Account',
      event_label: userType,
      user_type: userType,
      method: registrationMethod
    });
  }

  // Track user login
  trackUserLogin(userType, loginMethod = 'email') {
    this.trackEvent('login', {
      event_category: 'User Account',
      event_label: userType,
      user_type: userType,
      method: loginMethod
    });
  }

  // Set user properties
  setUserProperties(userId, userType, userProperties = {}) {
    this.pushToDataLayer({
      event: 'user_properties',
      user_id: userId,
      user_type: userType,
      ...userProperties
    });
  }

  // Track consent status
  trackConsentUpdate(consentStatus) {
    this.pushToDataLayer({
      event: 'consent_update',
      consent_analytics: consentStatus.analytics,
      consent_marketing: consentStatus.marketing,
      consent_preferences: consentStatus.preferences
    });
  }

  /*
   * MARKETING-SPECIFIC TRACKING METHODS
   */

  /**
   * Check if GTM is ready and warn if not (private helper)
   * @private
   * @param {string} context - Context for warning message
   * @returns {boolean} True if ready, false otherwise
   */
  _checkReady(context) {
    if (!this.isReady()) {
      if (gtmLoader.getConfig().debugMode) {
        console.warn(`⚠️ GTM not ready for ${context}`);
      }
      return false;
    }
    return true;
  }

  /**
   * Track lead form submission with comprehensive data
   * @param {string} formType - Type of form (e.g., 'lease_contact', 'loan_application', 'registration')
   * @param {Object} leadData - Lead information
   */
  trackLeadFormSubmission(formType, leadData = {}) {
    if (!this._checkReady(`lead submission: ${formType}`)) return;

    const {
      leadId,
      leadValue,
      entityName,
      contactName,
      email,
      phone,
      leadSource = 'website',
      additionalData = {}
    } = leadData;

    this.pushToDataLayer({
      event: 'lead_form_submission',
      event_category: 'Lead Generation',
      event_label: formType,
      form_type: formType,
      lead_id: leadId,
      lead_value: leadValue, // For conversion value tracking
      entity_name: entityName,
      contact_name: contactName,
      email_provided: !!email,
      phone_provided: !!phone,
      lead_source: leadSource,
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...additionalData
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log('📊 Lead Form Submission tracked:', formType, leadData);
    }
  }

  /**
   * Track conversion event with monetary value
   * @param {string} conversionName - Name of conversion (e.g., 'loan_approved', 'signup_complete')
   * @param {number} value - Monetary value of conversion
   * @param {Object} additionalData - Additional conversion data
   */
  trackConversion(conversionName, value = 0, additionalData = {}) {
    if (!this._checkReady(`conversion: ${conversionName}`)) return;

    this.pushToDataLayer({
      event: 'conversion',
      event_category: 'Conversion',
      event_label: conversionName,
      conversion_name: conversionName,
      conversion_value: value,
      currency: 'INR',
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...additionalData
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log('💰 Conversion tracked:', conversionName, value);
    }
  }

  /**
   * Track product/service interest
   * @param {string} productType - Type of product (e.g., 'pll', 'residential_loan', 'commercial_loan')
   * @param {Object} productData - Product-specific data
   */
  trackProductInterest(productType, productData = {}) {
    if (!this._checkReady(`product interest: ${productType}`)) return;

    this.pushToDataLayer({
      event: 'product_interest',
      event_category: 'Product',
      event_label: productType,
      product_type: productType,
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...productData
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log('🏷️ Product Interest tracked:', productType, productData);
    }
  }

  /**
   * Track funnel progression
   * @param {string} funnelName - Name of the funnel (e.g., 'loan_application', 'registration')
   * @param {string} stepName - Current step in the funnel
   * @param {number} stepNumber - Step number (1-based)
   * @param {Object} stepData - Step-specific data
   */
  trackFunnelStep(funnelName, stepName, stepNumber, stepData = {}) {
    if (!this._checkReady(`funnel step: ${funnelName}`)) return;

    this.pushToDataLayer({
      event: 'funnel_step',
      event_category: 'Funnel',
      event_label: `${funnelName} - ${stepName}`,
      funnel_name: funnelName,
      step_name: stepName,
      step_number: stepNumber,
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...stepData
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log(
        `📍 Funnel Step tracked: ${funnelName} - Step ${stepNumber}: ${stepName}`
      );
    }
  }

  /**
   * Track form interaction (start, field interaction, error, abandonment)
   * @param {string} formName - Name of the form
   * @param {string} interactionType - Type of interaction ('start', 'field_interaction', 'error', 'abandon')
   * @param {Object} interactionData - Interaction-specific data
   */
  trackFormInteraction(formName, interactionType, interactionData = {}) {
    if (!this._checkReady(`form interaction: ${formName}`)) return;

    this.pushToDataLayer({
      event: 'form_interaction',
      event_category: 'Form',
      event_label: `${formName} - ${interactionType}`,
      form_name: formName,
      interaction_type: interactionType,
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...interactionData
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log(
        `✍️ Form Interaction tracked: ${formName} - ${interactionType}`
      );
    }
  }

  /**
   * Track CTA (Call-to-Action) click
   * @param {string} ctaName - Name of the CTA
   * @param {string} ctaLocation - Location on page where CTA appears
   * @param {Object} ctaData - Additional CTA data
   */
  trackCTAClick(ctaName, ctaLocation = 'unknown', ctaData = {}) {
    if (!this._checkReady(`CTA click: ${ctaName}`)) return;

    this.pushToDataLayer({
      event: 'cta_click',
      event_category: 'CTA',
      event_label: ctaName,
      cta_name: ctaName,
      cta_location: ctaLocation,
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...ctaData
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log(`🖱️ CTA Click tracked: ${ctaName} at ${ctaLocation}`);
    }
  }

  /**
   * Track table/comparison view
   * @param {string} tableName - Name of the table
   * @param {Object} tableData - Table-specific data
   */
  trackTableView(tableName, tableData = {}) {
    if (!this._checkReady(`table view: ${tableName}`)) return;

    this.pushToDataLayer({
      event: 'table_view',
      event_category: 'Content Interaction',
      event_label: tableName,
      table_name: tableName,
      environment: process.env.REACT_APP_CURRENT_ENV,
      ...tableData
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log(`📋 Table View tracked: ${tableName}`);
    }
  }

  /**
   * Track phone number click (call tracking)
   * @param {string} phoneNumber - Phone number clicked (masked for privacy)
   * @param {string} location - Where the phone number appears
   */
  trackPhoneClick(phoneNumber, location = 'unknown') {
    if (!this._checkReady('phone click')) return;

    // Mask phone number for privacy (show only last 4 digits)
    const maskedPhone = phoneNumber
      ? `***-***-${phoneNumber.slice(-4)}`
      : 'unknown';

    this.pushToDataLayer({
      event: 'phone_click',
      event_category: 'Contact',
      event_label: maskedPhone,
      phone_location: location,
      environment: process.env.REACT_APP_CURRENT_ENV
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log(`📞 Phone Click tracked at ${location}`);
    }
  }

  /**
   * Track email click
   * @param {string} emailType - Type of email interaction
   * @param {string} location - Where the email link appears
   */
  trackEmailClick(emailType, location = 'unknown') {
    if (!this._checkReady('email click')) return;

    this.pushToDataLayer({
      event: 'email_click',
      event_category: 'Contact',
      event_label: emailType,
      email_type: emailType,
      email_location: location,
      environment: process.env.REACT_APP_CURRENT_ENV
    });

    if (gtmLoader.getConfig().debugMode) {
      console.log(`✉️ Email Click tracked: ${emailType} at ${location}`);
    }
  }

  /**
   * Track Google Ads conversion
   * Uses the Google Ads loader for direct conversion tracking
   */
  trackGoogleAdsConversion(conversionLabel, conversionData = {}) {
    if (!googleAdsLoader.isReady()) {
      if (googleAdsLoader.getConfig().debugMode) {
        console.warn('⚠️ Google Ads not ready for conversion tracking');
      }
      return;
    }

    googleAdsLoader.trackConversion(conversionLabel, conversionData);

    // Also push to dataLayer for GTM visibility
    this.pushToDataLayer({
      event: 'google_ads_conversion',
      conversion_label: conversionLabel,
      ...conversionData
    });
  }

  /**
   * Track Google Ads remarketing event
   */
  trackGoogleAdsRemarketing(eventName, eventData = {}) {
    if (!googleAdsLoader.isReady()) {
      if (googleAdsLoader.getConfig().debugMode) {
        console.warn('⚠️ Google Ads not ready for remarketing tracking');
      }
      return;
    }

    googleAdsLoader.trackRemarketingEvent(eventName, eventData);
  }

  // Environment management utilities

  // Get current GTM configuration
  getConfig() {
    return {
      ...gtmLoader.getConfig(),
      googleAdsConfig: googleAdsLoader.getConfig(),
      isServiceInitialized: this.isInitialized,
      isReady: this.isReady()
    };
  }

  // Environment validation
  validateEnvironment() {
    return gtmLoader.validateEnvironment();
  }

  // Enable debug mode
  enableDebugMode() {
    gtmLoader.setDebugMode(true);
    console.log('🔍 GTM Debug mode enabled');
  }

  // Disable debug mode
  disableDebugMode() {
    gtmLoader.setDebugMode(false);
    console.log('🔍 GTM Debug mode disabled');
  }

  // Force reinitialize (for development/testing)
  async reinitialize() {
    this.isInitialized = false;
    await gtmLoader.reload();
    return this.initialize();
  }
}

// Create singleton instance
const gtmService = new GTMService();

export default gtmService;

// Export helper functions for easy use
export const {
  initialize,
  trackPageView,
  trackEvent,
  trackClick,
  trackFormSubmit,
  trackScrollDepth,
  trackDownload,
  trackOutboundLink,
  trackVideoPlay,
  trackSearch,
  trackLoanApplicationStart,
  trackLoanApplicationComplete,
  trackCalculatorUse,
  trackUserRegistration,
  trackUserLogin,
  setUserProperties,
  trackConsentUpdate,
  // Marketing-specific methods
  trackLeadFormSubmission,
  trackConversion,
  trackProductInterest,
  trackFunnelStep,
  trackFormInteraction,
  trackCTAClick,
  trackTableView,
  trackPhoneClick,
  trackEmailClick,
  // Google Ads-specific methods
  trackGoogleAdsConversion,
  trackGoogleAdsRemarketing
} = gtmService;
